import { useMemo, useState } from 'react';
import type { Dayjs } from 'dayjs';
import {
  Alert,
  App,
  Button,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CorporateActionRecord,
  FreistellungsauftragSummary,
  TRADEVEST_FSA_MAX_AMOUNT,
  TradevestCorporateActionStatus,
  TradevestFsaType,
  TradevestTaxExemptionOrderStatus,
  effectiveFsaMaxAmount,
} from '@mygate/common';
import { usersService } from '../services/users';

const { Text } = Typography;

interface Props {
  userId: string;
  userName: string;
  isAdmin: boolean;
  open: boolean;
  onClose: () => void;
}

const CURRENT_YEAR = new Date().getUTCFullYear();

function euro(value?: number): string {
  if (value === undefined || value === null) return '—';
  return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

/** What the create form holds — antd DatePicker yields dayjs, the API wants ISO dates. */
interface FsaFormValues {
  fsaType: TradevestFsaType;
  fsaAmount: number;
  fsaValidFrom?: Dayjs;
  fsaValidUntil?: Dayjs;
}

function toCreatePayload(values: FsaFormValues): FsaCreatePayload {
  return {
    fsaType: values.fsaType,
    fsaAmount: values.fsaAmount,
    fsaValidFrom: values.fsaValidFrom?.format('YYYY-MM-DD'),
    fsaValidUntil: values.fsaValidUntil?.format('YYYY-MM-DD'),
  };
}

type FsaCreatePayload = {
  fsaType: TradevestFsaType;
  fsaAmount: number;
  fsaValidFrom?: string;
  fsaValidUntil?: string;
};

function fsaStatusColor(status: TradevestTaxExemptionOrderStatus): string {
  switch (status) {
    case TradevestTaxExemptionOrderStatus.ACTIVE:
      return 'green';
    case TradevestTaxExemptionOrderStatus.RECEIVED:
      return 'blue';
    case TradevestTaxExemptionOrderStatus.INVALID:
      return 'red';
    default:
      return 'default';
  }
}

function caStatusColor(status: TradevestCorporateActionStatus): string {
  switch (status) {
    case TradevestCorporateActionStatus.SETTLED:
      return 'green';
    case TradevestCorporateActionStatus.REJECTED:
      return 'red';
    case TradevestCorporateActionStatus.PENDING:
      return 'orange';
    default:
      return 'blue';
  }
}

/**
 * Staff view of a Tradevest customer's German taxes and corporate actions (MYG-476).
 *
 * The tax tab is where staff read the withheld KESt/Soli/KiSt and Verlusttöpfe, and — the write side
 * the mobile app deliberately does not expose — create or adjust the Freistellungsauftrag. Every FSA
 * write is Admin-only and audited server-side.
 */
export function TradevestTaxModal({ userId, userName, isAdmin, open, onClose }: Props) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [year, setYear] = useState(CURRENT_YEAR);
  const [fsaForm] = Form.useForm();

  const taxKey = ['tradevestTaxes', userId, year];
  const taxQuery = useQuery({
    queryKey: taxKey,
    queryFn: () => usersService.getTradevestTaxes(userId, year),
    enabled: open,
  });

  const caQuery = useQuery({
    queryKey: ['tradevestCorporateActions', userId],
    queryFn: () => usersService.getTradevestCorporateActions(userId),
    enabled: open,
  });

  const tax = taxQuery.data?.data;
  const info = tax?.information;
  /** The order that shelters income today — the only one worth editing. */
  const activeFsa = useMemo(
    () =>
      tax?.exemptionOrders.find(o => o.status === TradevestTaxExemptionOrderStatus.ACTIVE) ??
      tax?.exemptionOrders.find(o => o.status === TradevestTaxExemptionOrderStatus.RECEIVED) ??
      null,
    [tax],
  );
  /**
   * Tradevest validates asynchronously, so a submission that looked successful can end up INVALID
   * minutes later. Surface the newest rejection whenever nothing is in force — otherwise the order
   * vanishes and staff are shown the create form as if they had never filed one (MYG-498).
   */
  const rejectedFsa = useMemo(
    () =>
      activeFsa
        ? null
        : (tax?.exemptionOrders.find(o => o.status === TradevestTaxExemptionOrderStatus.INVALID) ?? null),
    [tax, activeFsa],
  );
  const totalWithheld = info
    ? info.withheldCapitalGainsTax + info.withheldSolidarityTax + info.withheldChurchTax
    : undefined;
  const remainingFsa =
    activeFsa && info ? Math.max(0, Math.round((activeFsa.fsaAmount - info.usedFsa) * 100) / 100) : undefined;

  const corporateActions = caQuery.data?.data?.items ?? [];

  const createFsa = useMutation({
    mutationFn: (values: FsaCreatePayload) =>
      usersService.createFreistellungsauftrag(userId, {
        ...values,
        reason: 'Backoffice FSA set',
      }),
    onSuccess: () => {
      message.success('Freistellungsauftrag submitted to Tradevest');
      fsaForm.resetFields();
      void queryClient.invalidateQueries({ queryKey: taxKey });
    },
    onError: (error: unknown) => message.error(error instanceof Error ? error.message : 'Failed to set FSA'),
  });

  const updateFsa = useMutation({
    mutationFn: (values: { taxExemptionOrderId: string; fsaAmount: number }) =>
      usersService.updateFreistellungsauftrag(userId, values.taxExemptionOrderId, {
        fsaAmount: values.fsaAmount,
        reason: 'Backoffice FSA update',
      }),
    onSuccess: () => {
      message.success('Freistellungsauftrag updated');
      void queryClient.invalidateQueries({ queryKey: taxKey });
    },
    onError: (error: unknown) => message.error(error instanceof Error ? error.message : 'Failed to update FSA'),
  });

  const cancelFsa = useMutation({
    mutationFn: (taxExemptionOrderId: string) =>
      usersService.cancelFreistellungsauftrag(userId, taxExemptionOrderId, 'Backoffice FSA cancellation'),
    onSuccess: response => {
      const { validUntil, retroactive } = response.data ?? {};
      message.success(
        retroactive
          ? `Freistellungsauftrag rückwirkend gekündigt (gültig bis ${validUntil})`
          : `Freistellungsauftrag zum ${validUntil} gekündigt`,
      );
      void queryClient.invalidateQueries({ queryKey: taxKey });
    },
    onError: (error: unknown) => message.error(error instanceof Error ? error.message : 'Failed to cancel FSA'),
  });

  // The staff endpoint returns a single tax year; offer the last few years to switch between.
  const yearOptions = useMemo(
    () => Array.from({ length: 4 }, (_, i) => CURRENT_YEAR - i).map(y => ({ label: String(y), value: y })),
    [],
  );

  return (
    <Modal
      title={`Tradevest — Steuern & Kapitalmaßnahmen · ${userName}`}
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
      width={860}
      destroyOnClose
    >
      <Tabs
        defaultActiveKey="taxes"
        items={[
          {
            key: 'taxes',
            label: 'Steuern',
            children: (
              <Spin spinning={taxQuery.isFetching}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Space>
                    <Text strong>Steuerjahr</Text>
                    <Select value={year} onChange={setYear} options={yearOptions} style={{ width: 120 }} />
                  </Space>

                  {info && (
                    <Descriptions bordered size="small" column={2}>
                      <Descriptions.Item label="Kapitalertragsteuer (KESt)">
                        {euro(info.withheldCapitalGainsTax)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Solidaritätszuschlag">
                        {euro(info.withheldSolidarityTax)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Kirchensteuer">{euro(info.withheldChurchTax)}</Descriptions.Item>
                      <Descriptions.Item label="Summe einbehalten">{euro(totalWithheld)}</Descriptions.Item>
                      <Descriptions.Item label="Verlusttopf Aktien">{euro(info.lossPotEquities)}</Descriptions.Item>
                      <Descriptions.Item label="Verlusttopf Sonstige">{euro(info.lossPotGeneral)}</Descriptions.Item>
                      <Descriptions.Item label="Genutzter Freibetrag (FSA)">{euro(info.usedFsa)}</Descriptions.Item>
                      <Descriptions.Item label="Anrechenbare ausländische Steuer">
                        {euro(info.creditedForeignWithholdingTax)}
                      </Descriptions.Item>
                    </Descriptions>
                  )}

                  <FreistellungsauftragSection
                    isAdmin={isAdmin}
                    activeFsa={activeFsa}
                    rejectedFsa={rejectedFsa}
                    remainingFsa={remainingFsa}
                    usedFsa={info?.usedFsa ?? 0}
                    allOrders={tax?.exemptionOrders ?? []}
                    fsaForm={fsaForm}
                    onCreate={values => createFsa.mutate(values)}
                    onUpdate={values => updateFsa.mutate(values)}
                    onCancel={taxExemptionOrderId => cancelFsa.mutate(taxExemptionOrderId)}
                    creating={createFsa.isPending}
                    updating={updateFsa.isPending}
                    cancelling={cancelFsa.isPending}
                  />
                </Space>
              </Spin>
            ),
          },
          {
            key: 'corporate-actions',
            label: `Kapitalmaßnahmen${corporateActions.length ? ` (${corporateActions.length})` : ''}`,
            children: (
              <Spin spinning={caQuery.isFetching}>
                <CorporateActionTable items={corporateActions} />
              </Spin>
            ),
          },
        ]}
      />
    </Modal>
  );
}

interface FsaSectionProps {
  isAdmin: boolean;
  activeFsa: FreistellungsauftragSummary | null;
  rejectedFsa: FreistellungsauftragSummary | null;
  remainingFsa?: number;
  /** Allowance already consumed this tax year — the floor Tradevest enforces on an update. */
  usedFsa: number;
  allOrders: FreistellungsauftragSummary[];
  fsaForm: ReturnType<typeof Form.useForm>[0];
  onCreate: (values: FsaCreatePayload) => void;
  onUpdate: (values: { taxExemptionOrderId: string; fsaAmount: number }) => void;
  onCancel: (taxExemptionOrderId: string) => void;
  creating: boolean;
  updating: boolean;
  cancelling: boolean;
}

function FreistellungsauftragSection({
  isAdmin,
  activeFsa,
  rejectedFsa,
  remainingFsa,
  usedFsa,
  allOrders,
  fsaForm,
  onCreate,
  onUpdate,
  onCancel,
  creating,
  updating,
  cancelling,
}: FsaSectionProps) {
  const [editAmount, setEditAmount] = useState<number | null>(activeFsa?.fsaAmount ?? null);
  const editMin = Math.max(1, usedFsa);
  // Mirrors `fsaCancellationValidUntil` on the backend so the confirm dialog states the real effect
  // rather than a generic "are you sure".
  const cancelYear = usedFsa > 0 ? CURRENT_YEAR : CURRENT_YEAR - 1;

  return (
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 16 }}>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Text strong>Freistellungsauftrag</Text>

        {activeFsa ? (
          <Descriptions size="small" column={2}>
            <Descriptions.Item label="Status">
              <Tag color={fsaStatusColor(activeFsa.status)}>{activeFsa.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Typ">{activeFsa.fsaType}</Descriptions.Item>
            <Descriptions.Item label="Betrag">{euro(activeFsa.fsaAmount)}</Descriptions.Item>
            <Descriptions.Item label="Verbleibend">{euro(remainingFsa)}</Descriptions.Item>
          </Descriptions>
        ) : (
          <Text type="secondary">Kein Freistellungsauftrag hinterlegt.</Text>
        )}

        {rejectedFsa && (
          <Alert
            type="error"
            showIcon
            message="Letzter Freistellungsauftrag von Tradevest abgelehnt"
            description={
              <>
                {euro(rejectedFsa.fsaAmount)} · {rejectedFsa.fsaType} · abgelehnt am{' '}
                {new Date(rejectedFsa.rejection?.notifiedAt ?? rejectedFsa.modifiedOn).toLocaleString('de-DE')}.
                {rejectedFsa.rejection ? (
                  <>
                    <br />
                    <Text strong>{rejectedFsa.rejection.errorType}</Text>
                    {rejectedFsa.rejection.description ? `: ${rejectedFsa.rejection.description}` : ''}
                  </>
                ) : (
                  // The reason only arrives on the webhook. Orders rejected before MYG-498 shipped —
                  // or whose notification was lost (webhooks are lossy in prod, MYG-416) — have none.
                  ' Kein Ablehnungsgrund übermittelt.'
                )}
                <br />
                Ein neuer Auftrag kann direkt angelegt werden.
              </>
            }
          />
        )}

        {isAdmin && activeFsa && (
          <Space>
            <InputNumber
              min={editMin}
              max={effectiveFsaMaxAmount(activeFsa.fsaType)}
              defaultValue={activeFsa.fsaAmount}
              addonAfter="€"
              onChange={value => setEditAmount(typeof value === 'number' ? value : null)}
            />
            <Popconfirm
              title="Freistellungsauftrag anpassen?"
              onConfirm={() =>
                editAmount && onUpdate({ taxExemptionOrderId: activeFsa.taxExemptionOrderId, fsaAmount: editAmount })
              }
            >
              <Button loading={updating}>Betrag ändern</Button>
            </Popconfirm>
            <Popconfirm
              title="Freistellungsauftrag kündigen?"
              description={
                usedFsa > 0
                  ? `${euro(usedFsa)} wurden bereits genutzt — Kündigung wirkt erst zum 31.12.${cancelYear}.`
                  : `Der Freibetrag wurde nicht genutzt — Kündigung wirkt rückwirkend zum 01.01.${CURRENT_YEAR}.`
              }
              okText="Kündigen"
              okButtonProps={{ danger: true }}
              onConfirm={() => onCancel(activeFsa.taxExemptionOrderId)}
            >
              <Button danger loading={cancelling}>
                Kündigen
              </Button>
            </Popconfirm>
            {usedFsa > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Nicht unter {euro(usedFsa)} (bereits genutzt) absenkbar.
              </Text>
            )}
          </Space>
        )}

        {isAdmin && !activeFsa && (
          <Form
            form={fsaForm}
            layout="inline"
            initialValues={{ fsaType: TradevestFsaType.INDIVIDUAL }}
            onFinish={values => onCreate(toCreatePayload(values as FsaFormValues))}
          >
            <Form.Item name="fsaType" rules={[{ required: true }]}>
              {/*
                JOINT is in Tradevest's enum but their create flow rejects it (release notes v1.8.0),
                asynchronously and out of band — so it is not offered here. Add the option back when
                Tradevest opens it up; `TRADEVEST_FSA_SUPPORTED_TYPES` is the single source of truth.
              */}
              <Select
                style={{ width: 160 }}
                options={[{ label: 'Einzel (max. 1.000 €)', value: TradevestFsaType.INDIVIDUAL }]}
              />
            </Form.Item>
            <Form.Item name="fsaAmount" rules={[{ required: true, message: 'Betrag erforderlich' }]}>
              <InputNumber min={1} max={TRADEVEST_FSA_MAX_AMOUNT} addonAfter="€" placeholder="Betrag" />
            </Form.Item>
            <Form.Item name="fsaValidFrom" tooltip="Leer = ab 01.01. des laufenden Jahres">
              <DatePicker placeholder="Gültig ab" format="DD.MM.YYYY" style={{ width: 150 }} />
            </Form.Item>
            <Form.Item
              name="fsaValidUntil"
              tooltip="Tradevest akzeptiert ausschließlich den 31.12.; leer = unbefristet"
            >
              <DatePicker
                placeholder="Gültig bis"
                format="DD.MM.YYYY"
                style={{ width: 150 }}
                disabledDate={date => !(date.month() === 11 && date.date() === 31)}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={creating}>
                Freistellungsauftrag anlegen
              </Button>
            </Form.Item>
          </Form>
        )}

        {isAdmin && !activeFsa && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Tradevest akzeptiert derzeit nur Einzel-Freistellungsaufträge bis 1.000 €. Gemeinsame Aufträge
            (Zusammenveranlagung, 2.000 €) sind noch nicht freigeschaltet.
          </Text>
        )}

        {!isAdmin && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Freistellungsauftrag kann nur von Admins geändert werden.
          </Text>
        )}

        {allOrders.length > 1 && (
          <Tooltip title="Alle Freistellungsaufträge (inkl. historischer)">
            <Text type="secondary" style={{ fontSize: 12 }}>
              {allOrders.length} Aufträge insgesamt
            </Text>
          </Tooltip>
        )}
      </Space>
    </div>
  );
}

function CorporateActionTable({ items }: { items: CorporateActionRecord[] }) {
  if (items.length === 0) {
    return <Empty description="Keine Kapitalmaßnahmen" />;
  }

  return (
    <Table<CorporateActionRecord>
      dataSource={items}
      rowKey={record => `${record.corporateActionId}`}
      size="small"
      pagination={{ pageSize: 10, hideOnSinglePage: true }}
      columns={[
        {
          title: 'Typ',
          dataIndex: 'eventType',
          render: (eventType: string, record) => (
            <Space size={4}>
              <Tag>{eventType}</Tag>
              {record.elective && <Tag color="purple">elektiv</Tag>}
            </Space>
          ),
        },
        { title: 'ISIN', dataIndex: 'isin', render: (isin: string, r) => r.assetName ?? isin },
        {
          title: 'Status',
          dataIndex: 'status',
          render: (status: TradevestCorporateActionStatus) => <Tag color={caStatusColor(status)}>{status}</Tag>,
        },
        {
          title: 'Netto',
          dataIndex: 'netAmount',
          align: 'right',
          render: (netAmount?: number) => euro(netAmount),
        },
        {
          title: 'Position',
          key: 'position',
          render: (_, record) =>
            record.oldQuantity !== undefined && record.newQuantity !== undefined
              ? `${record.oldQuantity} → ${record.newQuantity}`
              : '—',
        },
        {
          title: 'Zahltag',
          dataIndex: 'paymentDate',
          render: (paymentDate?: string) => (paymentDate ? new Date(paymentDate).toLocaleDateString('de-DE') : '—'),
        },
      ]}
      expandable={{
        expandedRowRender: record => (
          <Space direction="vertical" size={2} style={{ fontSize: 12 }}>
            {record.summary && <Text type="secondary">{record.summary}</Text>}
            {record.grossAmountPerShare !== undefined && <Text>Brutto/Anteil: {euro(record.grossAmountPerShare)}</Text>}
            {record.tax && (
              <Text>
                Steuer: KESt {euro(record.tax.germanCit)} · Soli {euro(record.tax.solidaritySurcharge)} · KiSt{' '}
                {euro(record.tax.churchTax)}
              </Text>
            )}
            {record.validationError && (
              <Text type="danger">
                {record.validationError.errorType}: {record.validationError.description}
              </Text>
            )}
            {record.responseDeadline && (
              <Text type="warning">Frist: {new Date(record.responseDeadline).toLocaleString('de-DE')}</Text>
            )}
          </Space>
        ),
        rowExpandable: record =>
          !!(record.summary || record.tax || record.validationError || record.grossAmountPerShare !== undefined),
      }}
    />
  );
}
